import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService } from '../toaster/toaster.service';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * The SPA never re-requests index.html, so a tab that stays open after a deploy keeps running the old bundle.
 * Polls index.html (served no-cache) and offers a reload when its script bundles differ from the loaded ones.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private started = false;
  private notified = false;
  private readonly loadedScripts = this.scriptSources(document.documentElement.outerHTML);

  constructor(
    private ngZone: NgZone,
    private toasterService: ToasterService,
    private translateService: TranslateService,
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    this.ngZone.runOutsideAngular(() => {
      setInterval(() => this.check(), CHECK_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.check();
      });
    });
  }

  private async check(): Promise<void> {
    if (this.notified) return;
    try {
      const response = await fetch('/index.html', { cache: 'no-store' });
      if (!response.ok) return;
      const latest = this.scriptSources(await response.text());
      if (latest.length && latest.join() !== this.loadedScripts.join()) {
        this.notified = true;
        this.ngZone.run(() => this.notify());
      }
    } catch {
      // Offline or transient failure: try again on the next check.
    }
  }

  private notify(): void {
    const ref = this.toasterService.show(
      this.translateService.instant('langs.new_version_available'),
      this.translateService.instant('langs.refresh'),
      0,
    );
    ref.onAction().subscribe(() => location.reload());
  }

  private scriptSources(html: string): string[] {
    const sources = [...html.matchAll(/<script[^>]+\ssrc="([^"]+)"/g)].map((m) => new URL(m[1], location.origin).pathname);
    return sources.filter((s) => /\/(main|polyfills|runtime)[.-]/.test(s)).sort();
  }
}
